
/* eslint-disable no-console , jsx-a11y/label-has-associated-control ,*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import { Container, Checkbox } from '@material-ui/core';
import * as Yup from 'yup';
import styles from './View.Style';
import Formbuilder from 'components_newui/formBuilder/View';

const validationSchema = Yup.object({
  templateName: Yup.string()
    .trim()
    .min(3, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters')
    .matches(
      /^[a-zA-Z0-9_ #!?|]*$/,
      'Only letters, numbers, spaces, underscore (_), hash (#), exclamation (!), question mark (?), and pipe (|) are allowed'
    )
    .required('Template Name is required'),
});

const CreateIdeaTemplate = (props) => {
  const {
    classes,
    myEmail,
    postIdeaTemplateCreateFn,
    updateIdeaTemplateLoading,      // kept in case you render a loader elsewhere
    createIdeaTemplateLoading,
  } = props || {};

  // --- Source-of-truth state only (no derived state here) ---
  const [templateName, setTemplateName] = useState('');
  const [templateActive, setTemplateActive] = useState(true);
  // From Formbuilder
  const [builderItems, setBuilderItems] = useState([]);   // previously ideaTemplatebuilderFormDataChildToParent
  const [builderRows, setBuilderRows] = useState([]);     // previously scorecardJSONRowFormData

  // --- UI state ---
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  // --- Derived values (via useMemo, not state) ---
  const weightRows = useMemo(
    () => (Array.isArray(builderItems) ? builderItems.filter((o) => o && ('weight' in o)) : []),
    [builderItems]
  );

  const totalWeight = useMemo(
    () =>
      weightRows.reduce((sum, r) => {
        const n = Number(r?.weight);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [weightRows]
  );

  const payload = useMemo(
    () => ({
      templateName: templateName.trim(),
      templateActive,
      totalWeight: String(totalWeight),
      // Keep as array in state; convert to string at submit only if API requires
      templateJson: builderRows,
    }),
    [templateName, templateActive, totalWeight, builderRows]
  );

  // --- Validate on relevant changes only ---
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        await validationSchema.validate({ templateName }, { abortEarly: false });
        if (isMounted) {
          setErrors({});
          setIsValid(true);
        }
      } catch (err) {
        const next = {};
        if (err?.inner?.length) {
          err.inner.forEach((e) => {
            if (e.path) next[e.path] = e.message;
          });
        } else if (err?.path) {
          next[err.path] = err.message;
        }
        if (isMounted) {
          setErrors(next);
          setIsValid(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [templateName]);

  // --- Handlers ---
  const handleNameChange = useCallback((e) => {
    setTemplateName(e.target.value);
  }, []);

  const handleActiveChange = useCallback((e) => {
    setTemplateActive(e.target.checked);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      await validationSchema.validate({ templateName }, { abortEarly: false });
      // If your API expects a JSON string for templateJson, uncomment the next line
      const apiPayload = {
        ...payload,
        // templateJson: JSON.stringify(payload.templateJson),
      };
      await postIdeaTemplateCreateFn?.(myEmail, apiPayload);
    } catch (error) {
      console.error('Error while saving template:', error);
    }
  }, [payload, postIdeaTemplateCreateFn, myEmail, templateName]);

  const isSubmitDisabled = createIdeaTemplateLoading || !isValid;

  // --- IDs for a11y ---
  const nameInputId = 'templateName';
  const nameErrorId = 'templateName-error';

  return (
    <Container maxWidth={false}>
      <div className={classes.createscorecardwrap}>
        <div className={classes.headingwrp}>
          <h2 className={classes.title}>Create Idea Template</h2>
        </div>

        <div className={classes.formbox}>
          <div className="block">
            <div className={classes.fieldbox}>
              <label htmlFor={nameInputId} className={classes.labels}>
                Template Name
              </label>
              <input
                className={classes.inputs}
                value={templateName}
                type="text"
                name="templateName"
                id={nameInputId}
                onChange={handleNameChange}
                maxLength={100}
                required
                aria-required="true"
                aria-invalid={Boolean(errors.templateName)}
                aria-describedby={errors.templateName ? nameErrorId : undefined}
                autoComplete="off"
              />
              {errors.templateName && (
                <p id={nameErrorId} className={classes.error}>
                  {errors.templateName}
                </p>
              )}
            </div>

            {/* If you want this toggle, keep it visible; otherwise remove both Checkbox import & this block */}
            {/* <div className={classes.fieldbox}>
              <label htmlFor="templateActive" className={classes.labels}>
                Make Template Active
              </label>
              <Checkbox
                id="templateActive"
                checked={templateActive}
                onChange={handleActiveChange}
                inputProps={{ 'aria-label': 'Make template active' }}
                name="templateActive"
                color="primary"
              />
            </div> */}
          </div>

          <div className={classes.overallWeight}>
            <p className={classes.labels}>Overall scoring weights</p>
            <div className={classes.overallBox}>
              <div className={classes.overallHeading}>
                <h4 className={classes.heading}>Question</h4>
                <h4 className={classes.heading}>Weight</h4>
              </div>

              {weightRows?.map((w, idx) => (
                <div key={w?.label} className={classes.overallWeightRow}>
                  <h4 className={classes.heading}>{w?.label ?? '-'}</h4>
                  <p className={classes.heading}>{w?.weight ?? 0}</p>
                </div>
              ))}

              <div className={classes.overallWeightRow}>
                <h4 className={classes.heading}>Total Weight</h4>
                <p className={classes.heading}>{totalWeight}</p>
              </div>
            </div>
          </div>
        </div>

        <h3>Create your template</h3>
        <Formbuilder
          isLoading={createIdeaTemplateLoading}
          onSave={handleSave}
          btnDisable={isSubmitDisabled}
          setScoreCardTemplatebuilderFormData={setBuilderItems}
          setScorecardJSONRowFormData={setBuilderRows}
        />
      </div>
    </Container>
  );
};

export default withStyles(styles)(CreateIdeaTemplate);
